import React from 'react'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
// @ts-ignore
import { WomanAndMan, Tooltip, QuestionMarkOutlined } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useInsightContentData } from 'hooks/useInsightModalState'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import { AvatarWithBadge, NameCell } from 'components/visualization/AuthorInfo'
import { AutomationId } from 'utils/automationUtil'
import TableChart from 'components/visualization/Table'
import { TopAuthorsTypeStrings } from 'components/insightContent/enum/TopAuthorsType'
import openHelpPage from 'utils/helpLinkUtil'
import LinkButton from 'components/share/LinkButton'
import { convertToTableData, findMaxValue, getHeaderText, getNumberFormatter } from '../utils'
import {
  StyledNoDataContent,
  NO_AUTHORS_FOUND_LIGHT,
  NO_AUTHORS_FOUND_DARK,
  StyledTableContainer,
  StyledAuthorInfoContainer,
  StyledSimpleBarContainer,
  StyledLeftHeaderContainer,
  StyledRightHeaderContainer,
  StyledIconBox,
  SIMPLE_BAR_PROGRESS_LIGHT,
  SIMPLE_BAR_PROGRESS_DARK
} from './index.css'
import LoadingRowGroup from './LoadingRowGroup'
import TableFrame from './TableFrame'

const InsightTable = ({ postType }: { postType: TopAuthorsTypeStrings }) => {
  const theme = useDisplayTheme() as Theme
  const insightContent = useInsightContentData(postType)
  const { t } = useTranslation()
  const tableData = convertToTableData(insightContent?.data)
  const maxVal = findMaxValue(tableData)

  return (
    <StyledTableContainer theme={theme}>
      {insightContent?.status === 'loading' ? (
        <TableFrame postType={postType}>
          <LoadingRowGroup />
        </TableFrame>
      ) : (
        <>
          {tableData.length === 0 ? (
            <TableFrame postType={postType}>
              <StyledNoDataContent
                mode={theme}
                data-testid={AutomationId.insightModal.insightContent.topAuthor.noAuthorFound}>
                <WomanAndMan
                  fontSize={64}
                  mode={theme}
                  color={theme === 'light' ? NO_AUTHORS_FOUND_LIGHT : NO_AUTHORS_FOUND_DARK}
                />
                {t('noAuthorsFound')}
              </StyledNoDataContent>
            </TableFrame>
          ) : (
            <TableChart
              rowData={tableData}
              colDefs={[
                {
                  field: 'authorName',
                  headerText: (
                    <StyledLeftHeaderContainer>{getHeaderText(postType)}</StyledLeftHeaderContainer>
                  ),
                  cellProps: {
                    textAlign: 'left',
                    displayTooltip: false,
                    width: '50%'
                  },
                  customCellRender: (_, { row }) => (
                    <StyledAuthorInfoContainer>
                      <AvatarWithBadge theme={theme} row={row} />
                      <NameCell row={row} theme={theme} />
                    </StyledAuthorInfoContainer>
                  )
                },
                {
                  field: 'followers',
                  headerText: (
                    <StyledRightHeaderContainer>
                      {t('followersVisitors')}
                      &nbsp;
                      <LinkButton onClick={() => openHelpPage(8292283)}>
                        <StyledIconBox mode={theme}>
                          <Tooltip
                            mode={theme}
                            content={<>{t('popularLinksInsightModalTooltip')}</>}>
                            <QuestionMarkOutlined color={sys[theme].text.placeholder} />
                          </Tooltip>
                        </StyledIconBox>
                      </LinkButton>
                    </StyledRightHeaderContainer>
                  ),
                  cellProps: {
                    width: '50%',
                    textAlign: 'left'
                  },
                  children: [
                    {
                      customCellRender: (cellValue: any) => (
                        <>{getNumberFormatter().format(cellValue!)}</>
                      ),
                      cellProps: {
                        textAlign: 'right',
                        fitWidth: true,
                        colSidePaddings: { left: '0px', right: '8px' }
                      }
                    },
                    {
                      customCellRender: (cellValue: any) => (
                        <StyledSimpleBarContainer>
                          <SimpleBar
                            data-testid={
                              AutomationId.insightModal.insightContent.topAuthor.simpleBar
                            }
                            progress={maxVal ? (cellValue / maxVal) * 100 : 0}
                            mode={theme}
                            colorSetting={
                              theme === 'light'
                                ? SIMPLE_BAR_PROGRESS_LIGHT
                                : SIMPLE_BAR_PROGRESS_DARK
                            }
                          />
                        </StyledSimpleBarContainer>
                      ),
                      cellProps: {
                        colSidePaddings: { left: '0px' }
                      }
                    }
                  ]
                }
              ]}
              isDisplayFoot={false}
            />
          )}
        </>
      )}
    </StyledTableContainer>
  )
}

export default InsightTable
